import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { ISettings } from '../../../../../../app/resource/settings/settings.types';
import { useApiRequestState } from '../../../../../../utils/api.utils';
import { integrateWrike, removeWrikeIntegration } from '../../../../../../app/page/page-storage/wrike/wrike.actions';
import { closeDialog, openDialog } from '../../../../../../app/dialog/dialog.actions';

const WRIKE = 'WRIKE';

export const WrikeIntegration = ({ settings }: { settings: ISettings }) => {
  const dispatch = useDispatch();

  const [showWrikeLoader] = useApiRequestState('integrateWrike');
  const [showRemoveLoader] = useApiRequestState('removeWrikeIntegration');
  const hasProcessedCallback = useRef(false);
  const previousLoaderState = useRef(showWrikeLoader);

  const handleRemoveIntegration = () => {
    dispatch(
      openDialog('confirmation', {
        title: 'Remove Wrike Integration',
        message: 'Are you sure you want to remove the Wrike integration? This will disconnect all synced folders.',
        onConfirm: () => {
          dispatch(removeWrikeIntegration());
          dispatch(closeDialog('confirmation'));
        },
      }),
    );
  };

  const handleWrikeCallback = useCallback(
    async (code: string, state: string) => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const stateParam = urlParams.get('state') || state;

        dispatch(integrateWrike(code, stateParam));
      } catch (error) {
        console.error('Error processing Wrike callback:', error);
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (hasProcessedCallback.current) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');
    const state = urlParams.get('state');
    const isWrikeCallback = state === WRIKE;

    if (code && isWrikeCallback) {
      hasProcessedCallback.current = true;
      handleWrikeCallback(code, state || '');
    } else if (error && isWrikeCallback) {
      hasProcessedCallback.current = true;
      console.error('Wrike OAuth error:', error);
    }
  }, [handleWrikeCallback]);

  // Clean URL after API response completes successfully
  useEffect(() => {
    // If loader was active and now is not, the API call completed
    if (previousLoaderState.current && !showWrikeLoader && hasProcessedCallback.current) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    previousLoaderState.current = showWrikeLoader;
  }, [showWrikeLoader]);

  const generateWrikeAuthUrl = () => {
    const url = new URL('https://login.wrike.com/oauth2/authorize/v4');
    url.searchParams.set('client_id', process.env.REACT_APP_WRIKE_CLIENT_ID || '');
    url.searchParams.set('response_type', 'code');
    url.searchParams.set('state', WRIKE);
    url.searchParams.set('redirect_uri', process.env.REACT_APP_WRIKE_REDIRECT_URI || '');
    return url.toString();
  };

  const handleWrikeConnect = () => {
    const authUrl = generateWrikeAuthUrl();
    window.location.href = authUrl;
  };

  if (showWrikeLoader) {
    return (
      <div className="row">
        <div className="col-12 pr-1">
          <div className="f-14 mb-2">Processing Wrike authorization...</div>
          <div className="flex align-center">
            <Loader type="TailSpin" color="var(--theme-primary-color)" height={20} width={20} />
            <span className="ml-2">Connecting to Wrike...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-bottom my-2">
      {settings?.wrikeWorkspaceName ? (
        <div className="row">
          <div className="col-12 pr-1">
            <div className="align-center mb-2 f-14 flex flex-start">
              Integrated with Wrike Workspace <span className="bold mx-1">{settings.wrikeWorkspaceName}</span>{' '}
              <img alt="Wrike" height="20" width="20" src="https://www.wrike.com/favicon.ico" />
            </div>
            <button
              className="flex align-center p-1 btn btn-danger my-2"
              onClick={handleRemoveIntegration}
              disabled={!!showRemoveLoader}
              style={{ pointerEvents: 'auto', height: 40 }}
              type="button"
            >
              {showRemoveLoader ? (
                <Loader type="TailSpin" color="#fff" height={20} width={20} />
              ) : (
                <span>Remove Integration</span>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12 pr-1">
            <div className="f-14 mb-2">Connect your Wrike workspace to sync tasks and projects</div>
            <button
              className="flex align-center p-1 btn white-bg btn-border"
              onClick={handleWrikeConnect}
              style={{ pointerEvents: 'auto', height: 40 }}
              type="button"
            >
              {showWrikeLoader ? (
                <Loader type="TailSpin" color="var(--theme-primary-color)" height={20} width={20} />
              ) : (
                <>
                  <img alt="Wrike" height="20" width="20" src="https://www.wrike.com/favicon.ico" />
                </>
              )}
              <span className="ml-2">Connect with Wrike</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
